import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { pick } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { NumberInput } from '@work4all/components/lib/input/number-input';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';

import { useDataMutation } from '@work4all/data';

import { CostCenter } from '@work4all/models/lib/Classes/CostCenter.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { OwnBankDetailsPickerField } from '../../../../../components/entity-picker/own-bank-details-picker-field/OwnBankDetailsPickerField';
import {
  GeneralSetting,
  useGeneralSetting,
} from '../../../../statistic/util/use-general-setting';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { useFormUpdate } from '../../utils/use-form-update';

type CostCenterFormValue = PathsOf<CostCenter, 2>;

const DEFAULT_VALUES = {};

export function CostCenterOverlayController(props: MaskControllerProps) {
  const { goBack } = useHistoryStack();

  const [mutate] = useDataMutation<CostCenter, EMode.upsert>({
    entity: Entities.costCenter,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    },
  });

  const mask = useMaskConfig(props);

  const request = useCostCenterRequestData(mask.id);

  const overlay = useMaskOverlay<CostCenterFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.name,
    id: Number(mask.id),
  });

  const { formState } = overlay.form;

  useFormUpdate(
    {
      bankDetails: (bankDetails) => ({ bankDetailsId: bankDetails?.id || 0 }),
    },
    overlay.form
  );

  const handleSubmit = useEventCallback(async (values: CostCenterFormValue) => {
    const update = mask.isCreateMode
      ? values
      : pick(values, ['id', ...Object.keys(formState.dirtyFields)]);

    await mutate(update);
    goBack({ force: true });
  });

  return (
    <OverlayController<CostCenterFormValue>
      {...overlay}
      onSubmit={handleSubmit}
    >
      <CostCenterMaskContent />
    </OverlayController>
  );
}

function CostCenterMaskContent() {
  const { t } = useTranslation();

  const { register, control } = useFormContextPlus<CostCenterFormValue>();

  const { data: settingsData } = useGeneralSetting({
    name: GeneralSetting.costCenterAsInvoiceNumberRange,
  });

  const setting = settingsData?.[0];

  const tenantAsCostCenter = Boolean(setting?.value);

  return (
    <>
      <Collapse title={t('COMMON.GENERAL')} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <ControllerPlus
                control={control}
                name="number"
                render={({ field: number }) => {
                  return (
                    <NumberInput
                      {...number}
                      decimals={0}
                      selectOnFocus
                      truncate
                      label={t('COMMON.NUMBER')}
                      hideControls
                      thousandSeparator=""
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.NAME')} {...register('name')} />
            </Grid>
            {tenantAsCostCenter && (
              <Grid item xs={12} sm={6} md={4}>
                <ControllerPlus
                  control={control}
                  name="lastNumber"
                  render={({ field: lastNumber }) => {
                    return (
                      <NumberInput
                        {...lastNumber}
                        decimals={0}
                        selectOnFocus
                        truncate
                        label={t('COMMON.COSTCENTER.LAST_NUMBER')}
                        hideControls
                        thousandSeparator=""
                      />
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.ADDITIVE')}
                {...register('additive')}
              />
            </Grid>
            {tenantAsCostCenter && (
              <Grid item xs={12} sm={6} md={4}>
                <ControllerPlus
                  control={control}
                  name="bankDetails"
                  render={({ field }) => {
                    return <OwnBankDetailsPickerField {...field} clearable />;
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <ControllerPlus
                control={control}
                name="disabled"
                render={({ field }) => (
                  <CheckboxRadioItem
                    {...field}
                    value={field.value ? '1' : '0'}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(Boolean(e.target.checked))}
                    label={t('COMMON.COSTCENTER.DISABLED')}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
      {tenantAsCostCenter && (
        <Collapse title={t('COMMON.FIRM_DATA')} defaultOpen>
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.USTID')}
                {...register('ustIdNr')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.FIRM_NAME')}
                {...register('firmName')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.STREET')}
                {...register('street')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.POSTAL_CODE')}
                {...register('postalCode')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.CITY')}
                {...register('city')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.COUNTRY')}
                {...register('country')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.STATE')}
                {...register('state')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.COSTCENTER.PHONE_NUMBER')}
                {...register('phoneNumber')}
              />
            </Grid>
          </Grid>
        </Collapse>
      )}
    </>
  );
}

export const useCostCenterRequestData = (id: string | number) => {
  return useMemo<DataRequest>(() => {
    const costCenterRequestData: CostCenter<EMode.query> = {
      id: null,
      name: null,
      number: null,
      lastNumber: null,
      disabled: null,
      additive: null,
      bankDetails: {
        id: null,
        iban: null,
        name: null,
        bic: null,
      },
      bankDetailsId: null,
      ustIdNr: null,
      firmName: null,
      street: null,
      postalCode: null,
      city: null,
      country: null,
      state: null,
      phoneNumber: null,
    };
    const filter = [{ id: { $eq: id } }];
    const data = costCenterRequestData;
    return {
      entity: Entities.costCenter,
      data,
      filter,
      completeDataResponse: false,
    };
  }, [id]);
};
